import { get } from '../request';

/**
 * 组件图鉴（Component Gallery）API 封装。
 * 对应后端 agent-server 的只读控制器 ComponentGalleryController。
 */

export interface AtomMeta {
  key: string;
  category: string;
  className: string;
  file: string;
  aliases: string[];
  description: string;
  alwaysInclude: boolean;
  compatibleWith: Record<string, string[]>;
  integration?: {
    templatePlan: boolean;
    blueprintPlan: boolean;
    assemblerCapability: boolean;
  };
}

export interface GalleryIntegration {
  templatePlanAtoms: string[];
  blueprintPlanAtoms: string[];
  unreferencedAtoms: string[];
  assemblerCapabilities: string[];
}

export interface PresentComponentMeta {
  id: string;
  kind: string;
  triggers: string[];
  priority: number;
  props?: Record<string, unknown>;
}

export interface ComponentDescriptor {
  id: string;
  version: string;
  layer: 'mechanics' | 'presentation' | 'feedback';
  category: string;
  aliases: string[];
  dependencies: string[];
  requiredCapabilities: string[];
  executable: boolean;
  source: 'atom' | 'present' | 'capability';
}

export interface CanvasUiComponentMeta {
  id: string;
  kind: string;
  description: string;
}

export interface EarnFxHookMeta {
  name: string;
  signature: string;
  description: string;
}

export interface EarnFxCapabilityMeta {
  id: string;
  name: string;
  kind: string;
  layer: string;
  description: string;
  hooks: EarnFxHookMeta[];
  drawEntry: string;
  stateExport: string;
  styles: string[];
  demoConfig: Record<string, unknown>;
}

export interface GalleryOverview {
  version: string;
  integration?: GalleryIntegration;
  descriptors: ComponentDescriptor[];
  aliases: Record<string, string[]>;
  atoms: AtomMeta[];
  illegalCombinations: Array<Record<string, string>>;
  present: PresentComponentMeta[];
  canvasUi: CanvasUiComponentMeta[];
  capabilities: EarnFxCapabilityMeta[];
}

export interface AtomSourcePayload {
  key: string;
  className: string;
  file: string;
  source: string;
}

export interface PresentRuntimePayload {
  source: string;
  components: PresentComponentMeta[];
  events: string[];
}

export interface EarnFxPayload {
  source: string;
  config: Record<string, unknown> & { redpacketStyle: string };
  hooks: EarnFxHookMeta[];
  drawEntry: string;
}

export interface CanvasUiPayload {
  source: string;
  components: CanvasUiComponentMeta[];
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

export async function fetchComponentOverview(): Promise<GalleryOverview> {
  const res = await get<Envelope<GalleryOverview>>('/api/component-gallery/overview');
  return res.data;
}

export async function fetchAtomSource(key: string): Promise<AtomSourcePayload> {
  const res = await get<Envelope<AtomSourcePayload>>(`/api/component-gallery/atoms/${encodeURIComponent(key)}/source`);
  return res.data;
}

export async function fetchPresentRuntime(): Promise<PresentRuntimePayload> {
  const res = await get<Envelope<PresentRuntimePayload>>('/api/component-gallery/present-runtime');
  return res.data;
}

export async function fetchEarnFxSource(style?: string): Promise<EarnFxPayload> {
  const query = style ? `?style=${encodeURIComponent(style)}` : '';
  const res = await get<Envelope<EarnFxPayload>>(`/api/component-gallery/earn-fx${query}`);
  return res.data;
}

export async function fetchCanvasUiSource(): Promise<CanvasUiPayload> {
  const res = await get<Envelope<CanvasUiPayload>>('/api/component-gallery/canvas-ui');
  return res.data;
}
